import { useState } from 'react';
import { useFinancialOverview, useFinancialTrends } from '../hooks/useFinancials';
import { FinancialsDashboard } from '../components/financials/FinancialsDashboard';
import { RevenueCostChart } from '../components/financials/RevenueCostChart';
import {
  exportFinancials,
  type ExportFormat,
  type LedgerPaymentMethod,
  type TrendRange,
} from '../lib/financialsApi';
import { FadeIn, SkeletonChart, SkeletonStats, SkeletonTable } from '../components/ui/Skeleton';
import { Select } from '../components/ui/Select';
import { MultiSelect } from '../components/ui/MultiSelect';
import { FinancialAnalytics } from '../components/financials/FinancialAnalytics';
import { CompanyMultiSelect } from '../components/financials/CompanyMultiSelect';
import { useActiveCompanyId } from '../store/useCompanyStore';
import { useI18n } from '../contexts/I18nContext';

const CURRENT_YEAR = new Date().getUTCFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
const RANGE_VALUES: TrendRange[] = ['3m', '6m', '1y', 'all'];

const TABS = ['overview', 'analytics'] as const;
type Tab = (typeof TABS)[number];

export function FinancialsPage() {
  const { t } = useI18n();
  // Persist the active tab in the URL hash (#analytics), like ProjectDetailPage.
  const initialTab = (window.location.hash.replace('#', '') as Tab) || 'overview';
  const [tab, setTab] = useState<Tab>(TABS.includes(initialTab) ? initialTab : 'overview');
  // Multi-select periods (CLE): [] = all years / all months.
  const [years, setYears] = useState<number[]>([CURRENT_YEAR]);
  const [months, setMonths] = useState<number[]>([]);
  const [compare, setCompare] = useState(true);
  const [method, setMethod] = useState<LedgerPaymentMethod | undefined>(undefined);
  const [range, setRange] = useState<TrendRange>('1y');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Firm multi-select (multi-company): [] = all firms. Initial selection is
  // derived from the global sidebar switcher, then managed locally.
  const activeCompanyId = useActiveCompanyId();
  const [companyIds, setCompanyIds] = useState<string[]>(activeCompanyId ? [activeCompanyId] : []);

  function selectTab(next: Tab) {
    setTab(next);
    window.location.hash = next;
  }

  const overview = useFinancialOverview({ years, months, compare, method, companyIds });
  const trends = useFinancialTrends(range, 'month', method, companyIds);

  async function handleExport(format: ExportFormat) {
    setExporting(true);
    setExportError(null);
    try {
      // The export endpoint works on a single period: pass year/month only
      // when exactly one of each is selected, otherwise fall back to overview.
      const exportYear = years.length === 1 ? years[0] : undefined;
      const exportMonth = months.length === 1 ? months[0] : undefined;
      await exportFinancials({
        format,
        report: exportYear !== undefined && exportMonth !== undefined ? 'monthly' : 'overview',
        year: exportYear,
        month: exportMonth,
        range,
        companyIds: companyIds.length > 0 ? companyIds : undefined,
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : t('financials.exportFailed'));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('financials.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* Firm multi-select (multi-company) — hidden with ≤1 firm. */}
          <CompanyMultiSelect
            value={companyIds}
            onChange={setCompanyIds}
            className="w-full sm:w-52"
          />
          {tab === 'overview' && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleExport('csv')}
                disabled={exporting}
                className="rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('financials.exportCsv')}
              </button>
              <button
                type="button"
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                className="rounded bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {t('financials.exportPdf')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs: classic P&L overview vs dimensional analytics (services). */}
      <div className="mt-6 flex gap-4 border-b border-gray-200">
        {TABS.map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => selectTab(tb)}
            className={`-mb-px border-b-2 pb-2 text-sm font-medium ${
              tab === tb
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t(`financials.tab.${tb}`)}
          </button>
        ))}
      </div>

      {tab === 'analytics' && (
        <section className="mt-6">
          <FinancialAnalytics companyIds={companyIds.length > 0 ? companyIds : undefined} />
        </section>
      )}

      {tab === 'overview' && (
        <>
          {exportError && <p className="mt-3 text-sm text-red-600">{exportError}</p>}

          {/* Period selector */}
          <div className="mt-6 flex flex-wrap items-end gap-3">
            <MultiSelect
              label={t('financials.yearLabel')}
              allLabel={t('financials.allYears')}
              summaryLabel={(count) => t('financials.yearsSelected', { count })}
              value={years.map(String)}
              onChange={(vs) => setYears(vs.map(Number))}
              options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
              className="w-full sm:w-36"
            />

            <MultiSelect
              label={t('financials.monthLabel')}
              allLabel={t('financials.allMonths')}
              summaryLabel={(count) => t('financials.monthsSelected', { count })}
              value={months.map(String)}
              onChange={(vs) => setMonths(vs.map(Number))}
              options={Array.from({ length: 12 }, (_, i) => i + 1).map((m) => ({
                value: String(m),
                label: t(`financials.month.${m}`),
              }))}
              className="w-full sm:w-40"
            />

            <Select
              label={t('paymentMethod.label')}
              clearable
              value={method ?? ''}
              onChange={(v) => setMethod((v || undefined) as LedgerPaymentMethod | undefined)}
              placeholder={t('paymentMethod.all')}
              options={(['BANK_TRANSFER', 'CARD', 'CASH'] as const).map((m) => ({
                value: m,
                label: t(`paymentMethod.${m}`),
              }))}
              className="w-full sm:w-40"
            />

            <label className="inline-flex items-center gap-2 pb-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={compare}
                onChange={(e) => setCompare(e.target.checked)}
                className="ui-checkbox"
              />
              {t('financials.compare')}
            </label>
          </div>

          {/* P&L metrics */}
          <section className="mt-6">
            {overview.isLoading && (
              <div className="space-y-6">
                <SkeletonStats count={4} />
                <SkeletonTable rows={6} columns={4} />
              </div>
            )}
            {overview.isError && <p className="text-red-600">{t('financials.error.overview')}</p>}
            {overview.data && (
              <FadeIn>
                <FinancialsDashboard overview={overview.data} />
              </FadeIn>
            )}
          </section>

          {/* Trends */}
          <section className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('financials.revenueVsCosts')}
              </h2>
              <Select
                label={t('financials.revenueVsCosts')}
                hideLabel
                value={range}
                onChange={(v) => setRange(v as TrendRange)}
                options={RANGE_VALUES.map((r) => ({ value: r, label: t(`financials.range.${r}`) }))}
                className="w-full sm:w-40"
              />
            </div>

            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              {trends.isLoading && (
                <SkeletonChart bars={12} height={240} className="border-0 p-0" />
              )}
              {trends.isError && <p className="text-red-600">{t('financials.error.trends')}</p>}
              {trends.data && (
                <FadeIn>
                  <RevenueCostChart
                    actuals={trends.data.dataPoints}
                    forecast={trends.data.forecast}
                  />
                </FadeIn>
              )}
            </div>
            {trends.data && trends.data.forecast.length > 0 && (
              <p className="mt-2 text-sm text-gray-500">
                {t('financials.forecastNote', { count: trends.data.forecast.length })}
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
